import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const COOKIE_PREFS_KEY = 'cookie_preferences';

/**
 * TrackingScripts - Loads Google Analytics, Google Ads, and Meta Pixel
 * based on admin settings and user cookie consent.
 *
 * - GA4: Loads only if user accepted 'analiticos' cookies
 * - Google Ads: Loads only if user accepted 'marketing' cookies
 * - Meta Pixel: Loads only if user accepted 'marketing' cookies
 * - Search Console: Meta tag is always rendered (no cookies needed)
 *
 * Tracks SPA page navigation via React Router's useLocation.
 */
export default function TrackingScripts() {
  const location = useLocation();
  const integrations = useRef(null);
  const initialized = useRef(false);

  // Fetch active integrations from backend
  useEffect(() => {
    fetch(`${API}/api/v2/seo/integrations/public`)
      .then(r => r.json())
      .then(data => {
        integrations.current = data.integrations || {};
        initScripts();
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track page changes in SPA
  useEffect(() => {
    if (!initialized.current) return;
    const intg = integrations.current;
    if (!intg) return;

    // GA4 pageview
    if (intg.google_analytics_id && window.gtag) {
      window.gtag('config', intg.google_analytics_id, {
        page_path: location.pathname + location.search,
      });
    }

    // Meta Pixel pageview
    if (intg.meta_pixel_id && window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location]);

  function getCookiePrefs() {
    try {
      const raw = localStorage.getItem(COOKIE_PREFS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function loadScript(src, id) {
    return new Promise((resolve, reject) => {
      if (document.getElementById(id)) { resolve(); return; }
      const script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function addInlineScript(id, code) {
    if (document.getElementById(id)) return;
    const script = document.createElement('script');
    script.id = id;
    script.textContent = code;
    document.head.appendChild(script);
  }

  function addMetaTag(name, content) {
    const id = `meta-${name}`;
    if (document.getElementById(id)) return;
    const meta = document.createElement('meta');
    meta.id = id;
    meta.name = name;
    meta.content = content;
    document.head.appendChild(meta);
  }

  function initScripts() {
    const intg = integrations.current;
    if (!intg || initialized.current) return;
    initialized.current = true;

    const prefs = getCookiePrefs();

    // Google Search Console verification (always, no cookie needed)
    if (intg.search_console_verification) {
      addMetaTag('google-site-verification', intg.search_console_verification);
    }

    // Google Analytics GA4 (requires analiticos consent)
    if (intg.google_analytics_id && prefs?.analiticos) {
      const gaId = intg.google_analytics_id;

      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', gaId, {
        page_path: window.location.pathname,
        anonymize_ip: true,
      });

      loadScript(`https://www.googletagmanager.com/gtag/js?id=${gaId}`, 'ga4-script');
    }

    // Google Ads (requires marketing consent)
    if (intg.google_ads_id && prefs?.marketing) {
      const adsId = intg.google_ads_id;

      if (!window.gtag) {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
      }
      window.gtag('config', adsId);

      loadScript(`https://www.googletagmanager.com/gtag/js?id=${adsId}`, 'gads-script');
    }

    // Meta Pixel (requires marketing consent)
    if (intg.meta_pixel_id && prefs?.marketing) {
      const pixelId = intg.meta_pixel_id;

      addInlineScript('fb-pixel-init', `
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
        n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
        (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
        fbq('init','${pixelId}');
        fbq('track','PageView');
      `);
    }
  }

  // No visible UI
  return null;
}

/**
 * Helper: Track custom events from any component.
 * Usage:
 *   import { trackEvent } from '../components/TrackingScripts';
 *   trackEvent('view_business', { business_name: 'Padaria do Zé' });
 */
export function trackEvent(eventName, params = {}) {
  // GA4
  if (window.gtag) {
    window.gtag('event', eventName, params);
  }
  // Meta Pixel
  if (window.fbq) {
    window.fbq('trackCustom', eventName, params);
  }
}

/**
 * Helper: Track standard Meta Pixel events.
 * Usage:
 *   import { trackMetaEvent } from '../components/TrackingScripts';
 *   trackMetaEvent('Lead');
 *   trackMetaEvent('ViewContent', { content_name: 'Padaria do Zé' });
 */
export function trackMetaEvent(eventName, params = {}) {
  if (window.fbq) {
    window.fbq('track', eventName, params);
  }
}

/**
 * Helper: Track Google Ads conversions.
 * Usage:
 *   import { trackGoogleAdsConversion } from '../components/TrackingScripts';
 *   trackGoogleAdsConversion('AW-123456789/abcDEF');
 */
export function trackGoogleAdsConversion(conversionLabel) {
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: conversionLabel,
    });
  }
}
